/**
 * Where a link into a run's Conversation lands (spec 149 FR-006): the run, and
 * optionally one message on its thread, in the address's hash.
 *
 * The hash rather than a search parameter: the drawer is hosted by three
 * routes (the board, the Tasks page, the run page), each with its own search
 * schema, and a notification already carries a hash to its target
 * (`NotificationTarget.hash`). Nothing in the page has these ids, so the
 * router's own scroll-to-hash finds nothing and leaves the landing to the
 * Runs tab.
 */
export interface RunThreadFocus {
  executionId: string;
  messageId: string | null;
}

const PREFIX = 'run-';
const MESSAGE = '-message-';
/** Ids are UUIDs: fixed length, so the run id is read without a separator search. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `run-{executionId}`, or `run-{executionId}-message-{messageId}`, without the `#`. */
export function runThreadHash(
  executionId: string,
  messageId?: string | null,
): string {
  return messageId
    ? `${PREFIX}${executionId}${MESSAGE}${messageId}`
    : `${PREFIX}${executionId}`;
}

/** The run and message a hash names, with or without its `#`; `null` for any other hash. */
export function parseRunThreadHash(hash: string): RunThreadFocus | null {
  const value = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!value.startsWith(PREFIX)) return null;
  const executionId = value.slice(PREFIX.length, PREFIX.length + 36);
  if (!UUID.test(executionId)) return null;
  const rest = value.slice(PREFIX.length + 36);
  if (rest === '') return { executionId, messageId: null };
  if (!rest.startsWith(MESSAGE)) return null;
  const messageId = rest.slice(MESSAGE.length);
  return UUID.test(messageId) ? { executionId, messageId } : null;
}

/** The element a message is drawn as, which a landing scrolls into view. */
export function threadMessageElementId(messageId: string): string {
  return `thread-message-${messageId}`;
}
